// Story 11.11 — the country VAT register at /admin/vat-register.
// Wrapped by <AdminGuard> in App.tsx. Owner requirement, verbatim: "I also NEED
// monitoring/managing these country VAT rules within the admin panel."
//
// It is a TOOL, not a document: scanned, then operated. So the summary is above
// the detail, every state is a chip as well as a number, and the two write
// actions sit between them — the register is read to find the gap, and the forms
// are how the gap is closed.
//
// ⛔ IT RESTATES NO EUROS. What is held, what has been released and the worst
// case belong to Story 11.9's exposure surface, which reads them off the ledger
// accounts themselves. This page shows the POSTURE — which of the four things
// happens to the money when a top-up arrives from a jurisdiction — and links to
// that surface for the amounts. A euro figure copied here would be the stale
// one.
//
// Design: no new palette, no new typeface, no new dependency. `Card`/
// `CardContent`/`Badge`/`Button`/`Input`/`Label`/`Textarea`, the
// `border-brand-forest-200` select styling, the `text-xs uppercase tracking-wide
// opacity-70` label idiom, `font-heading` and `tabular-nums` are all lifted from
// AdminMetricsPage's sibling panels, so this is the existing admin surface with
// one more page on it.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Textarea,
} from "@greenhold/components";

import {
  useRecordVatRateRow,
  useRecordVatStatusRow,
  useVatRegister,
  type JurisdictionView,
  type ProvisionPosture,
  type RecordRuleError,
  type RegisterRuleView,
  type ReportingRail,
  type RuleStatus,
  type VatRegisterOverview,
} from "../features/admin/useVatRegister";

// ---------------------------------------------------------------------------
// Vocabulary — the operator's words for the register's stored values.
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
  eu_member_state: "EU member state",
  researched_rate: "Researched rate",
  researched_zero: "Researched, zero-rated",
  researched_special: "Researched, no single rate",
  hard_blocked: "Hard-blocked",
  unresearched: "Unresearched",
};

const RAIL_LABELS: Record<string, string> = {
  eu_oss: "EU One Stop Shop",
  domestic: "Danish return",
  own_rail: "Its own registration",
  special: "Not a single return",
};

/// ⚑ The four postures, in the operator's words. Each says what happens to the
/// MONEY, because that is the question the register is read to answer.
const POSTURE: Record<
  ProvisionPosture,
  {
    label: string;
    tone: "default" | "secondary" | "success" | "warning" | "destructive";
    blurb: string;
  }
> = {
  provisioned_at_setting: {
    label: "Reserved at the default",
    tone: "warning",
    blurb:
      "Nobody has researched this jurisdiction, so every top-up from it is accepted, charged 0%, and a reserve is held against it at the platform's default rate.",
  },
  own_rail_hedge: {
    label: "Hedged at its own rate",
    tone: "default",
    blurb:
      "This jurisdiction reports on its own rail, so its duty runs on its own clock whatever the Danish registration says. Every top-up is reserved at this row's own rate.",
  },
  own_rail_rate_missing: {
    label: "Own rail, no rate — nothing reserved",
    tone: "destructive",
    blurb:
      "A broken rule, not a treatment. This jurisdiction reports on its own rail but the rule in force names no rate, so nothing is set aside against a duty that is still running, and every top-up from here raises an alert. Record a rule with its rate, or move it off its own rail.",
  },
  registered_booked: {
    label: "Charged and booked",
    tone: "success",
    blurb:
      "Researched, and Greenhold is registered: VAT is charged on the top-up and booked to this state. Nothing is reserved, because nothing is uncertain.",
  },
  researched_no_provision: {
    label: "Researched, nothing reserved",
    tone: "secondary",
    blurb:
      "Researched, and nothing is held back — either because Greenhold is not registered yet, or because the research came back with no single national rate to charge.",
  },
};

function formatBps(bps: number | null): string {
  if (bps === null) return "—";
  return `${(bps / 100).toFixed(2).replace(/\.?0+$/, "")}%`;
}

// ---------------------------------------------------------------------------
// The summary strip
// ---------------------------------------------------------------------------

function SummaryTile({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4 space-y-1">
        <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
        <p className="text-2xl font-heading font-bold tabular-nums">{value}</p>
        {detail ? <p className="text-xs opacity-70">{detail}</p> : null}
      </CardContent>
    </Card>
  );
}

function Summary({ data }: { data: VatRegisterOverview }) {
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <SummaryTile
        label="Jurisdictions"
        value={String(data.jurisdiction_count)}
        detail={`${data.rule_count} rules on file`}
      />
      <SummaryTile
        label="Sources past re-check"
        value={String(data.stale_jurisdiction_count)}
        detail={`older than ${data.staleness_threshold_days} days`}
      />
      <SummaryTile
        label="Typed by hand"
        value={String(data.manual_rule_count)}
        detail="the rest came from the country files"
      />
      <SummaryTile
        label="Registration"
        value={
          data.registration_state === "registered" ? "Registered" : "Not registered"
        }
        detail={`default reserve ${formatBps(data.provision_rate_bps)} of gross`}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Withheld countries — the reason this page exists
// ---------------------------------------------------------------------------

/// The two halves of the loader's withheld list, told apart by the register.
///
/// ⛔ A COUNTRY THE FILE WITHHOLDS BUT THE REGISTER CARRIES IS NOT
/// UNRESEARCHED. The moment a rule is typed in here, this page would otherwise
/// go on advertising that country as accepted-at-0%-and-reserved-at-the-default
/// — a false statement about live money, made by the page that had just caused
/// it. The server reconciles the two; these are the two sentences.
function WithheldCard({ data }: { data: VatRegisterOverview }) {
  if (data.withheld_unavailable) {
    return (
      <Card>
        <CardContent className="p-5 space-y-2">
          <h2 className="font-heading font-semibold">Withheld countries</h2>
          <p className="text-sm opacity-80 leading-relaxed">
            The research files could not be read, so this list is unknown rather
            than empty: <code className="text-xs">{data.withheld_unavailable}</code>
          </p>
        </CardContent>
      </Card>
    );
  }
  if (data.withheld.length === 0 && data.research_owes_an_update.length === 0) {
    return null;
  }

  return (
    <>
      {data.withheld.length > 0 ? (
        <Card data-testid="withheld-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-heading font-semibold">
                Researched, but withheld from the register
              </h2>
              {data.withheld.map((w) => (
                <Badge key={w.country} variant="warning">
                  {w.country}
                </Badge>
              ))}
            </div>
            <p className="text-sm opacity-80 leading-relaxed">
              These countries have a research file whose rate is not{" "}
              <em>established</em> — a primary source could not be retrieved, or
              its commencement date could not be — and nothing is in force for
              them in the register either. So they resolve as unresearched: their
              top-ups are accepted, charged 0%, and reserved at the default rate.
              Open the primary source yourself, then record the rate below with
              the URL you read and the date you read it.
            </p>
            <ul className="text-xs font-mono opacity-70">
              {data.withheld.map((w) => (
                <li key={w.country}>{w.research_artifact}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {data.research_owes_an_update.length > 0 ? (
        <Card data-testid="owes-research-update-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-heading font-semibold">
                Typed in here — the country file still owes an update
              </h2>
              {data.research_owes_an_update.map((w) => (
                <Badge key={w.country} variant="secondary">
                  {w.country}
                </Badge>
              ))}
            </div>
            <p className="text-sm opacity-80 leading-relaxed">
              A rule <strong>is</strong> in force for these countries and it
              governs what their top-ups are charged — they are not unresearched
              and nothing is reserved at the default rate for them. What is
              outstanding is the research: their file still records the rate as
              unconfirmed. Write the same source and date into it, so the number
              on a return traces to a primary source without anyone being asked.
            </p>
            <ul className="text-xs opacity-70">
              {data.research_owes_an_update.map((w) => (
                <li key={w.country}>
                  <span className="font-mono">{w.research_artifact}</span>
                  {" — register applies "}
                  {w.register_rate_bps === null
                    ? "no single rate"
                    : formatBps(w.register_rate_bps)}
                  {w.register_effective_from
                    ? ` from ${w.register_effective_from}`
                    : ""}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// The two write actions
// ---------------------------------------------------------------------------

const RATE_STATUSES: RuleStatus[] = [
  "eu_member_state",
  "researched_rate",
  "researched_zero",
];
const RATELESS_STATUSES: RuleStatus[] = ["researched_special", "hard_blocked"];
const RAILS: ReportingRail[] = ["eu_oss", "domestic", "own_rail", "special"];

/// The maintenance rule, stated where the typing happens. ⛔ COPY ONLY — nothing
/// here enforces it, and nothing here writes to the repository.
function MaintenanceReminder({ country }: { country: string }) {
  const file = `docs/vat-jurisdictions/${(country.trim() || "cc").toLowerCase()}.md`;
  return (
    <p className="text-xs opacity-70 leading-relaxed">
      A rule typed here does <strong>not</strong> update the research. Record the
      same source and date in <code className="font-mono">{file}</code> — the
      register follows the country files, and a rule with no file behind it is a
      number nobody can trace back to a primary source.
    </p>
  );
}

function RuleForm({
  title,
  intro,
  statuses,
  requiresRate,
  mutation,
  idPrefix,
  maxRateBps,
}: {
  title: string;
  intro: string;
  statuses: RuleStatus[];
  requiresRate: boolean;
  mutation:
    | ReturnType<typeof useRecordVatRateRow>
    | ReturnType<typeof useRecordVatStatusRow>;
  idPrefix: string;
  maxRateBps: number;
}) {
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState<RuleStatus>(statuses[0]);
  const [ratePct, setRatePct] = useState("");
  const [rail, setRail] = useState<ReportingRail>(
    requiresRate ? "eu_oss" : "special"
  );
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [checkedOn, setCheckedOn] = useState("");
  const [note, setNote] = useState("");

  // Rates are typed as a PERCENT because that is how every statute states them,
  // and converted to basis points here — the one place the conversion happens.
  const rateBps = requiresRate ? Math.round(Number(ratePct) * 100) : null;
  const rateLooksValid =
    !requiresRate ||
    (ratePct.trim().length > 0 &&
      Number.isFinite(rateBps) &&
      rateBps !== null &&
      rateBps >= 0 &&
      rateBps <= maxRateBps);

  const canSubmit =
    country.trim().length === 2 &&
    effectiveFrom.length > 0 &&
    sourceUrl.trim().length > 0 &&
    checkedOn.length > 0 &&
    rateLooksValid &&
    !mutation.isPending;

  function submit() {
    mutation.mutate(
      {
        country: country.trim().toUpperCase(),
        status,
        rate_bps: rateBps,
        reporting_rail: rail,
        effective_from: effectiveFrom,
        source_url: sourceUrl.trim(),
        source_checked_on: checkedOn,
        note: note.trim().length > 0 ? note.trim() : null,
      },
      {
        onSuccess: () => {
          setNote("");
          setRatePct("");
        },
      }
    );
  }

  const error = mutation.error as RecordRuleError | null;

  return (
    <Card data-testid={`${idPrefix}-form`}>
      <CardContent className="p-5 space-y-4">
        <div className="space-y-1">
          <h3 className="font-heading font-semibold">{title}</h3>
          <p className="text-sm opacity-70 leading-relaxed">{intro}</p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}_country`}>Country (ISO code)</Label>
            <Input
              id={`${idPrefix}_country`}
              value={country}
              maxLength={2}
              placeholder="BE"
              className="font-mono uppercase"
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}_status`}>Status</Label>
            <select
              id={`${idPrefix}_status`}
              className="block w-full rounded-md border border-brand-forest-200 bg-white p-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as RuleStatus)}
            >
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>

          {requiresRate ? (
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}_rate`}>Standard rate (%)</Label>
              <Input
                id={`${idPrefix}_rate`}
                inputMode="decimal"
                placeholder="21"
                className="tabular-nums"
                value={ratePct}
                onChange={(e) => setRatePct(e.target.value)}
              />
              <p className="text-xs opacity-60">
                As the statute states it. Stored as {rateBps ?? 0} basis points;
                the register accepts 0% to {maxRateBps / 100}%.
              </p>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}_rail`}>Reported on</Label>
            <select
              id={`${idPrefix}_rail`}
              className="block w-full rounded-md border border-brand-forest-200 bg-white p-2 text-sm"
              value={rail}
              onChange={(e) => setRail(e.target.value as ReportingRail)}
            >
              {RAILS.map((r) => (
                <option key={r} value={r}>
                  {RAIL_LABELS[r]}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}_effective_from`}>Effective from</Label>
            <Input
              id={`${idPrefix}_effective_from`}
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
            <p className="text-xs opacity-60">
              The date the rule commences. A future date is fine — an announced
              change is added before it takes effect.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}_checked_on`}>Source read on</Label>
            <Input
              id={`${idPrefix}_checked_on`}
              type="date"
              value={checkedOn}
              onChange={(e) => setCheckedOn(e.target.value)}
            />
            <p className="text-xs opacity-60">
              The day you actually opened it — not the day it was published.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}_source_url`}>Primary source</Label>
          <Input
            id={`${idPrefix}_source_url`}
            type="url"
            placeholder="https://financien.belgium.be/…"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}_note`}>
            Note <span className="font-normal opacity-60">(optional)</span>
          </Label>
          <Textarea
            id={`${idPrefix}_note`}
            rows={2}
            value={note}
            placeholder="Why this rule exists — e.g. read at the counter after every online endpoint refused an automated client."
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <MaintenanceReminder country={country} />

        <p className="text-xs opacity-70 leading-relaxed">
          Nothing here is ever edited or deleted. A wrong rule is superseded by a
          new one with a <strong>later</strong> effective date; the payments
          already taken keep the treatment they were charged under.
        </p>

        {error ? (
          <p
            className="text-sm text-state-error-fg"
            role="alert"
            data-testid={`${idPrefix}-error`}
          >
            {error.message}
          </p>
        ) : null}
        {mutation.isSuccess ? (
          <p className="text-sm opacity-80" data-testid={`${idPrefix}-ok`}>
            Recorded {mutation.data.data.country} effective{" "}
            {mutation.data.data.effective_from}
            {mutation.data.data.in_force_now ? " (in force now)" : " (not yet in force)"};
            audit row {mutation.data.data.audit_log_id}.
          </p>
        ) : null}

        <Button variant="primary" onClick={submit} disabled={!canSubmit}>
          {mutation.isPending ? "Recording…" : "Record rule"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The register table
// ---------------------------------------------------------------------------

function StalenessCell({
  days,
  stale,
}: {
  days: number | null;
  stale: boolean;
}) {
  if (days === null) {
    // ⛔ An honest "no date on this row" is not a large number of days. Showing
    // one would invent a re-check that never happened.
    return <span className="opacity-60">no date</span>;
  }
  return (
    <>
      <span className="tabular-nums">{days}d</span>
      {stale ? (
        <>
          {" "}
          <Badge variant="warning">re-check</Badge>
        </>
      ) : null}
    </>
  );
}

function RuleHistoryRow({ rule }: { rule: RegisterRuleView }) {
  return (
    <tr className="border-t border-border/60 text-xs">
      <td className="py-1 pl-6 pr-4 tabular-nums">{rule.effective_from}</td>
      <td className="py-1 pr-4">{STATUS_LABELS[rule.status] ?? rule.status}</td>
      <td className="py-1 pr-4 text-right tabular-nums">
        {rule.rate_bps === null ? (
          <span className="opacity-60">no single rate</span>
        ) : (
          formatBps(rule.rate_bps)
        )}
      </td>
      <td className="py-1 pr-4">
        {RAIL_LABELS[rule.reporting_rail] ?? rule.reporting_rail}
      </td>
      <td className="py-1 pr-4">
        {rule.source_url ? (
          <a
            href={rule.source_url}
            target="_blank"
            rel="noreferrer noopener"
            className="underline decoration-dotted break-all"
          >
            {rule.source_url}
          </a>
        ) : (
          <span className="font-mono opacity-70">{rule.research_artifact}</span>
        )}
      </td>
      <td className="py-1 pr-4">
        <StalenessCell days={rule.staleness_days} stale={rule.stale} />
      </td>
      <td className="py-1">
        {rule.in_force ? <Badge variant="success">in force</Badge> : null}{" "}
        {rule.manual ? <Badge variant="secondary">typed</Badge> : null}
      </td>
    </tr>
  );
}

function JurisdictionRows({
  jurisdiction,
  expanded,
  onToggle,
}: {
  jurisdiction: JurisdictionView;
  expanded: boolean;
  onToggle: () => void;
}) {
  const posture = POSTURE[jurisdiction.posture];
  return (
    <tbody data-testid="register-jurisdiction">
      <tr className="border-t border-border">
        <td className="py-1.5 pr-4 font-mono font-semibold">
          {jurisdiction.country}
        </td>
        <td className="py-1.5 pr-4">
          {jurisdiction.status ? (
            STATUS_LABELS[jurisdiction.status] ?? jurisdiction.status
          ) : (
            <span className="opacity-60">not yet in force</span>
          )}
        </td>
        <td className="py-1.5 pr-4 text-right tabular-nums font-semibold">
          {jurisdiction.rate_bps === null ? (
            <span className="font-normal opacity-60">no single rate</span>
          ) : (
            formatBps(jurisdiction.rate_bps)
          )}
        </td>
        <td className="py-1.5 pr-4">
          {jurisdiction.reporting_rail
            ? RAIL_LABELS[jurisdiction.reporting_rail] ??
              jurisdiction.reporting_rail
            : "—"}
        </td>
        <td className="py-1.5 pr-4 tabular-nums">
          {jurisdiction.effective_from ?? "—"}
        </td>
        <td className="py-1.5 pr-4">
          <StalenessCell
            days={jurisdiction.staleness_days}
            stale={jurisdiction.stale}
          />
        </td>
        <td className="py-1.5 pr-4">
          <Badge variant={posture.tone}>{posture.label}</Badge>
        </td>
        <td className="py-1.5 text-right">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="text-xs underline decoration-dotted opacity-80"
          >
            {expanded ? "Hide" : "History"} ({jurisdiction.rules.length})
            {jurisdiction.future_rule_count > 0
              ? ` · ${jurisdiction.future_rule_count} pending`
              : ""}
          </button>
        </td>
      </tr>
      {expanded
        ? jurisdiction.rules.map((rule) => (
            <RuleHistoryRow
              key={`${jurisdiction.country}-${rule.effective_from}`}
              rule={rule}
            />
          ))
        : null}
    </tbody>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function AdminVatRegisterPage() {
  const register = useVatRegister();
  const rateMutation = useRecordVatRateRow();
  const statusMutation = useRecordVatStatusRow();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [onlyStale, setOnlyStale] = useState(false);

  const data = register.data?.data;
  const jurisdictions = useMemo(() => {
    if (!data) return [];
    return onlyStale
      ? data.jurisdictions.filter((j) => j.stale)
      : data.jurisdictions;
  }, [data, onlyStale]);

  return (
    <div className="max-w-5xl space-y-6 p-6" data-testid="admin-vat-register-page">
      <header className="space-y-1">
        <h1 className="font-heading text-2xl font-semibold">
          Country VAT register
        </h1>
        <p className="text-sm opacity-70 leading-relaxed">
          Which rule applies to a top-up from where, since when, and on whose
          authority. Rules are forward-only: a change is a new rule with its own
          effective date, and every payment keeps the rule that was in force when
          it was taken.
        </p>
      </header>

      {register.isPending ? (
        <p className="text-sm opacity-70">Loading the register…</p>
      ) : null}
      {register.isError ? (
        <Card>
          <CardContent className="p-5 text-state-error-fg" role="alert">
            {register.error.message}
          </CardContent>
        </Card>
      ) : null}

      {data ? (
        <>
          <Summary data={data} />

          <p className="text-sm opacity-70 leading-relaxed">
            This page carries no amounts. What is actually held against each
            jurisdiction, what has been released, and the worst case are on the{" "}
            <Link
              to={data.exposure_surface_path}
              className="underline decoration-dotted"
              data-testid="exposure-surface-link"
            >
              VAT exposure panel
            </Link>
            , which reads them straight off the ledger.
          </p>

          <WithheldCard data={data} />

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <RuleForm
              title="Add a rate"
              intro="A jurisdiction's standard rate, from the date it commenced. Use this when a primary source has been read and the rate is settled."
              statuses={RATE_STATUSES}
              requiresRate
              mutation={rateMutation}
              idPrefix="vat-rate-row"
              maxRateBps={data.max_plausible_rate_bps}
            />
            <RuleForm
              title="Add a treatment"
              intro="A jurisdiction whose answer is not a single national rate, or one you are marking hard-blocked. It carries no rate and points at its research instead."
              statuses={RATELESS_STATUSES}
              requiresRate={false}
              mutation={statusMutation}
              idPrefix="vat-status-row"
              maxRateBps={data.max_plausible_rate_bps}
            />
          </div>

          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-heading font-semibold">
                  The register, as of {data.as_of}
                </h2>
                <label className="flex items-center gap-2 text-xs opacity-80">
                  <input
                    type="checkbox"
                    checked={onlyStale}
                    onChange={(e) => setOnlyStale(e.target.checked)}
                    data-testid="only-stale"
                  />
                  Only sources past re-check
                </label>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left opacity-70">
                      <th className="py-1 pr-4 font-medium">Country</th>
                      <th className="py-1 pr-4 font-medium">Status</th>
                      <th className="py-1 pr-4 font-medium text-right">Rate</th>
                      <th className="py-1 pr-4 font-medium">Reported on</th>
                      <th className="py-1 pr-4 font-medium">In force since</th>
                      <th className="py-1 pr-4 font-medium">Source age</th>
                      <th className="py-1 pr-4 font-medium">
                        What happens to the money
                      </th>
                      <th className="py-1 font-medium text-right">Rules</th>
                    </tr>
                  </thead>
                  {jurisdictions.map((j) => (
                    <JurisdictionRows
                      key={j.country}
                      jurisdiction={j}
                      expanded={Boolean(expanded[j.country])}
                      onToggle={() =>
                        setExpanded((prev) => ({
                          ...prev,
                          [j.country]: !prev[j.country],
                        }))
                      }
                    />
                  ))}
                </table>
              </div>

              {jurisdictions.length === 0 ? (
                <p className="text-sm opacity-70">
                  {onlyStale
                    ? "Every source has been re-read inside the threshold."
                    : "The register is empty — no country file has produced a rule."}
                </p>
              ) : null}

              <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs opacity-70 sm:grid-cols-2">
                {(Object.keys(POSTURE) as ProvisionPosture[]).map((key) => (
                  <div key={key} className="flex gap-2">
                    <dt className="shrink-0">
                      <Badge variant={POSTURE[key].tone}>
                        {POSTURE[key].label}
                      </Badge>
                    </dt>
                    <dd className="leading-relaxed">{POSTURE[key].blurb}</dd>
                  </div>
                ))}
              </dl>

              <p className="text-xs opacity-60 italic leading-relaxed">
                A source is flagged for re-check after{" "}
                {data.staleness_threshold_days} days. That is a prompt, not a
                verdict: nothing resolves differently and no reserve changes
                because a row is old. The re-check pass is annual, in the first
                working week of January — rates in the EU overwhelmingly commence
                on 1 January.
              </p>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
